import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import InvoiceDetails from './InvoiceDetails';
import {
  Receipt as ReceiptIcon,
  OpenInNew as OpenInNewIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  CurrencyRupee as RupeeIcon,
  ChevronLeft,
  ChevronRight,
  Summarize as SummarizeIcon,
  AccountBalanceWallet as WalletIcon,
  Savings as SavingsIcon,
} from '@mui/icons-material';
import { useBillingAndInvoice } from '../../../contexts/BillingAndInvoiceContext';
import { formatCurrency, getInvoiceStatusColor } from '../../../utils/helper';

const InvoiceList = ({ showSuccessMessage }) => {
  const { 
    invoices, 
    summary, 
    loading, 
    error, 
    filterOptions,
    fetchInvoices, 
    fetchInvoiceDetail,
    fetchFilterOptions,
  } = useBillingAndInvoice();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Invoice details modal state
  const [invoiceDetailsOpen, setInvoiceDetailsOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceDetail, setSelectedInvoiceDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Load initial data on component mount
   useEffect(() => {
    fetchInvoices();
  }, []);


  useEffect(() => {
    fetchFilterOptions();
  }, []);


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceDetailsOpen(true);
    setDetailLoading(true);
    
    try {
      const response = await fetchInvoiceDetail(invoice.id);
      setSelectedInvoiceDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      if (showSuccessMessage) {
        showSuccessMessage('Failed to load invoice details', 'error');
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseInvoiceDetails = () => {
    setInvoiceDetailsOpen(false);
    setSelectedInvoice(null);
    setSelectedInvoiceDetail(null);
  };

  // Filter invoices based on search term and status
  const filteredInvoices = invoices.filter((invoice) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch =
      term === '' ||
      invoice.invoice_number?.toLowerCase().includes(term) ||
      invoice.description?.toLowerCase().includes(term);
    const matchesStatus = selectedStatus === 'all' || invoice.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = filteredInvoices.length === 0 ? 0 : Math.ceil(filteredInvoices.length / rowsPerPage);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentInvoices = filteredInvoices.slice(startIndex, endIndex);

  // Reset to first page only when user changes search or status filter (not on every invoices refetch)
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedStatus]);

  // Clamp page when it goes out of bounds (e.g. after filtering or changing rows per page)
  useEffect(() => {
    if (filteredInvoices.length === 0) {
      setPage(0);
    } else if (page >= totalPages) {
      setPage(totalPages - 1);
    }
  }, [filteredInvoices.length, totalPages, page]);


  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setSelectedStatus(status);
    // Status filtering is now handled locally in the filteredInvoices logic
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    // Prevent navigation beyond valid pages
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(0); // Reset to first page on search
  };


  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Invoice summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.light',
              height: '100%',
              bgcolor: 'rgba(0, 112, 80, 0.06)',
              borderLeft: '4px solid',
              borderLeftColor: 'primary.main',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <SummarizeIcon sx={{ fontSize: 22, color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Total Invoice Generated
              </Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
              {summary.total_invoices_generated || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total invoices generated
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
              bgcolor: 'rgba(0, 0, 0, 0.02)',
              borderLeft: '4px solid',
              borderLeftColor: 'info.main',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <WalletIcon sx={{ fontSize: 22, color: 'info.main' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Total Spent
              </Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={700} color="info.dark">
              {formatCurrency(summary.total_spent || 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total amount spent
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'success.light',
              height: '100%',
              bgcolor: 'rgba(25, 135, 84, 0.1)',
              borderLeft: '4px solid',
              borderLeftColor: 'success.main',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <SavingsIcon sx={{ fontSize: 22, color: 'success.main' }} />
              <Typography variant="body2" color="success.dark" fontWeight={600}>
                Total Savings
              </Typography>
            </Box>
            <Typography variant="subtitle1" fontWeight={700} color="success.dark">
              {formatCurrency(summary.total_savings || 0)}
            </Typography>
            <Typography variant="caption" color="success.dark" sx={{ opacity: 0.9 }}>
              From discounts
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Invoice Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <ReceiptIcon />
                </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Invoices
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredInvoices.length} invoices generated
                </Typography>
              </Box>
            </Box>
          </Box>



          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Search by invoice number, description"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          Loading invoices...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : currentInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          No invoices found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm || selectedStatus !== 'all' ? 'No invoices match your search criteria.' : 'There are no invoices available.'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentInvoices.map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {formatDate(invoice.created_at)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {invoice.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(invoice.taxable_invoice_value)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status_verbose}
                          color={getInvoiceStatusColor(invoice.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              color: 'text.primary',
                              textTransform: 'none',
                              minWidth: 'auto',
                              py: 0.25,
                              px: 0.5,
                              fontSize: '0.7rem',
                              '& .MuiButton-startIcon': { mr: 0.25 },
                            }}
                            onClick={() => handleViewInvoice(invoice)}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              color: 'text.primary',
                              textTransform: 'none',
                              minWidth: 'auto',
                              py: 0.25,
                              px: 0.5,
                              fontSize: '0.7rem',
                              '& .MuiButton-startIcon': { mr: 0.25 },
                            }}
                            onClick={() => {
                              if (invoice.invoice_url) {
                                const link = document.createElement('a');
                                link.href = invoice.invoice_url;
                                link.download = `${invoice.invoice_number || 'invoice'}.pdf`;
                                link.target = '_blank';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            }}
                          >
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination – below table */}
          {!loading && filteredInvoices.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                mt: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  mr: 0.5,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Rows per page:
              </Typography>
              <Select
                value={rowsPerPage}
                size="small"
                sx={{
                  minWidth: '65px',
                  height: '30px',
                  '& .MuiSelect-select': { py: 0.5, fontWeight: 500 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                }}
                onChange={handleChangeRowsPerPage}
                variant="outlined"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
              <Typography variant="body2" sx={{ fontWeight: 500, mx: 1 }}>
                {totalPages > 0
                  ? `${startIndex + 1}–${Math.min(endIndex, filteredInvoices.length)} of ${filteredInvoices.length}`
                  : '0 of 0'}
              </Typography>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', borderLeft: '1px solid', borderColor: 'divider', pl: 1, ml: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 0}
                    sx={{ color: page <= 0 ? 'text.disabled' : 'primary.main' }}
                    aria-label="Previous page"
                  >
                    <ChevronLeft fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages - 1}
                    sx={{ color: page >= totalPages - 1 ? 'text.disabled' : 'primary.main' }}
                    aria-label="Next page"
                  >
                    <ChevronRight fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Paper>
          )}

        </CardContent>
      </Card>

      {/* Invoice Details Modal */}
      {invoiceDetailsOpen && (
        <InvoiceDetails
          open={invoiceDetailsOpen}
          onClose={handleCloseInvoiceDetails}
          invoice={selectedInvoiceDetail || selectedInvoice}
          showSuccessMessage={showSuccessMessage}
          loading={detailLoading}
        />
      )}
    </Box>
  );
};

export default InvoiceList; 